import { FC, useMemo, useState, useEffect } from "react";
import {
  MRT_ColumnDef,
  MRT_PaginationState,
  MantineReactTable,
  useMantineReactTable,
} from "mantine-react-table";
import { VirtualAccountTransactions } from "../../../services/getVirtualAccountsTransactions";
import {
  convertDate,
  getDateFromMonthsAgo,
} from "@funded-here-interface/common/src/Utils/date";

import { toast } from "react-toastify";
import useGetVATransaction from "../../../hooks/useGetVATransactions";
import { Box, Button } from "@mantine/core";
import { DateInput } from "@mantine/dates";
import { IconDownload } from "@tabler/icons-react";
import useDownloadVATransactionsCSV from "../../../hooks/useDownloadVATransactionsCSV";
import { downloadFile } from "@funded-here-interface/common/src/Utils/file";
import { FileType } from "@funded-here-interface/common/src/constant/enum";
import { RootState } from "../../../store";
import { useSelector } from "react-redux";

const Transactions: FC = () => {
  const useDownloadVATransactionsCSVMutation = useDownloadVATransactionsCSV();
  const token = useSelector((state: RootState) => state.auth.token);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [startDate, setStartDate] = useState<Date>(getDateFromMonthsAgo(3));
  const [endDate, setEndDate] = useState<Date>(new Date());

  //store pagination state in your own state
  const [pagination, setPagination] = useState<MRT_PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const formatDateForAPI = (date: Date): string => {
    return date.toISOString().split("T")[0];
  };

  const { data, error, isLoading } = useGetVATransaction(
    token,
    pagination,
    formatDateForAPI(startDate),
    formatDateForAPI(endDate)
  );

  const totalRowCount = data?.meta?.totalRowCount ?? 0;

  if (error) {
    toast.error((error as Error).message);
  }

  const handleExportData = () => {
    setIsExportLoading(true);
    useDownloadVATransactionsCSVMutation.mutate(
      {
        token,
        startDate: formatDateForAPI(startDate),
        endDate: formatDateForAPI(endDate),
      },
      {
        onSuccess: (data: Blob) => {
          setIsExportLoading(false);

          downloadFile(data, "VA Transactions", FileType.CSV);
        },
        onError: (e) => {
          setIsExportLoading(false);

          toast.error((e as Error).message);
        },
      }
    );
  };

  const columns = useMemo<MRT_ColumnDef<VirtualAccountTransactions>[]>(
    () => [
      {
        id: "sender",
        header: "Sender details",
        mantineTableHeadCellProps: {
          sx: {
            backgroundColor: "#a6cbed",
          },
        },
        columns: [
          {
            accessorKey: "senderRef",
            header: "Sender Reference",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderBankAccountId",
            header: "Sender Bank Account Number",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderAccountId",
            header: "Sender Virtual Account ID",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderRefId",
            header: "Sender Reference ID",
          },
          {
            accessorKey: "senderStructuredNoteId",
            header: "Sender Note ID",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderType",
            header: "Sender Virtual Account Type",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderBalanceBefore",
            header: "Sender VA Opening Balance",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "senderBalanceAfter",
            header: "Sender VA Closing Balance",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
        ],
      },
      {
        id: "receiver",
        header: "Receiver details",
        mantineTableHeadCellProps: {
          sx: {
            backgroundColor: "#D9D9D9",
          },
        },
        columns: [
          {
            accessorKey: "receiverRef",
            header: "Receiver Reference",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "receiverBankAccountId",
            header: "Receiver Bank Account Number",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "receiverAccountId",
            header: "Receiver Virtual Account ID",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "receiverRefId",
            header: "Receiver Reference ID",
          },
          {
            accessorKey: "receiverStructuredNoteId",
            header: "Receiver Note ID",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "receiverType",
            header: "Receiver Virtual Account Type",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "receiverBalanceBefore",
            header: "Receiver VA Opening Balance",
          },
          {
            accessorKey: "receiverBalanceAfter",
            header: "Receiver VA Closing Balance",
          },
        ],
      },
      {
        id: "basic",
        header: "",
        columns: [
          {
            accessorKey: "currency",
            header: "Currency",
          },
          {
            accessorKey: "amount",
            header: "Amount",
          },
          {
            accessorKey: "otherCurrency",
            header: "Other Currency",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "amountOtherCurrency",
            header: "Amount (Other Currency)",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "feesIncurred",
            header: "Fee Incurred",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "status",
            header: "Status",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
          {
            accessorKey: "createdDate",
            header: "Date",
            Cell: ({ cell }) => <p>{convertDate(cell.getValue<string>())}</p>,
          },
          {
            accessorKey: "remarks",
            header: "Remarks",
            Cell: ({ renderedCellValue }) =>
              renderedCellValue ? (
                <span>{renderedCellValue}</span>
              ) : (
                <div>&#8210;</div>
              ),
          },
        ],
      },
    ],
    []
  );

  const table = useMantineReactTable({
    enableColumnActions: false,
    enableColumnFilters: false,
    enableDensityToggle: false,
    // paginationDisplayMode: "pages",
    state: { pagination, isLoading }, //pass the pagination and isLoading state to the table
    columns,
    data: data?.data ?? [],
    manualPagination: true,
    onPaginationChange: setPagination, //hoist pagination state to your state when it changes internally
    rowCount: totalRowCount, //pass the total row count to the table
    initialState: { density: "xs" },
    mantineTableProps: {
      withColumnBorders: true,
    },
    mantineTableHeadCellProps: {
      style: {
        padding: "0.5rem 1.0rem",
        color: "#23262a",
      },
    },
    mantineTableBodyCellProps: {
      style: {
        padding: "0.5rem",
      },
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <Box
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
          alignItems: "flex-end",
        }}
      >
        <DateInput
          label="Start Date"
          placeholder="Select start date"
          value={startDate}
          onChange={(value) => {
            setStartDate(value ?? getDateFromMonthsAgo(3));
            setPagination({ pageIndex: 0, pageSize: pagination.pageSize }); // Reset to first page on filter change
          }}
          maxDate={endDate || undefined}
          clearable
          style={{ width: 200 }}
        />
        <DateInput
          label="End Date"
          placeholder="Select end date"
          value={endDate}
          onChange={(value) => {
            setEndDate(value ?? new Date());
            setPagination({ pageIndex: 0, pageSize: pagination.pageSize }); // Reset to first page on filter change
          }}
          clearable
          minDate={startDate || undefined}
          style={{ width: 200 }}
        />
        <Button
          color="lightblue"
          //export all data that is currently in the table (ignore pagination, sorting, filtering, etc.)
          onClick={handleExportData}
          leftIcon={<IconDownload />}
          variant="filled"
          loading={isExportLoading}
        >
          Export
        </Button>
      </Box>
    ),
  });

  return (
    <div>
      <MantineReactTable table={table} />
    </div>
  );
};

export default Transactions;
